import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext();

// Décode le token JWT en utilisateur connecté (null si absent ou invalide)
const decodeUser = (token) => {
    if (!token) return null;
    try {
        const decoded = jwtDecode(token);
        return {
            id: decoded.id || decoded.userId,
            username: decoded.username,
            avatar: decoded.avatar,
            adminGuilds: decoded.adminGuilds || [],
            memberGuilds: decoded.memberGuilds || [],
        };
    } catch {
        return null;
    }
};

// Token illisible ou expiré (le backend le refuserait)
const isExpired = (token) => {
    try {
        const { exp } = jwtDecode(token);
        return !!exp && exp * 1000 <= Date.now();
    } catch {
        return true;
    }
};

// Serveur (admin ou membre) de l'utilisateur correspondant à serverId
export const findUserGuild = (user, serverId) =>
    [...(user?.adminGuilds || []), ...(user?.memberGuilds || [])].find(g => g.id === serverId);

export const AuthProvider = ({ children }) => {
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(true);
    const [redirectServerId, setRedirectServerId] = useState(null); // Ajout d'un état pour stocker l'ID du serveur de destination
    const user = useMemo(() => decodeUser(token), [token]);

    useEffect(() => {
        
        // Désactiver le mode développement pour tester l'auth réelle
        // if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        //     console.log('🔓 Mode développement détecté: bypass de l\'authentification');
        //     const devToken = 'dev-token-local-development';
        //     localStorage.setItem('DISCORD_BOT_TOKEN', devToken);
        //     setToken(devToken);
        //     setLoading(false);
        //     return;
        // }

        const storedToken = localStorage.getItem('DISCORD_BOT_TOKEN');
        if (storedToken && !isExpired(storedToken)) {
            setToken(storedToken);
        } else if (storedToken) {
            localStorage.removeItem('DISCORD_BOT_TOKEN');
        }
        setLoading(false);
    }, []);

    const login = (newToken) => {
        localStorage.setItem('DISCORD_BOT_TOKEN', newToken);
        setToken(newToken);
    };

    const logout = () => {
        localStorage.removeItem('DISCORD_BOT_TOKEN');
        setToken(null);
    };

    // Fonction pour définir le serverId de destination
    const setRedirectTarget = (serverId) => {
        setRedirectServerId(serverId);
        // Stocker en session storage pour persister pendant le processus d'OAuth
        sessionStorage.setItem('REDIRECT_SERVER_ID', serverId || '');
    };

    // Fonction pour récupérer et effacer la destination
    const getAndClearRedirectTarget = () => {
        const target = redirectServerId || sessionStorage.getItem('REDIRECT_SERVER_ID');
        // Effacer après utilisation
        setRedirectServerId(null);
        sessionStorage.removeItem('REDIRECT_SERVER_ID');
        return target;
    };

    return (
        <AuthContext.Provider value={{ 
            token,
            user,
            login,
            logout, 
            loading, 
            setRedirectTarget, 
            getAndClearRedirectTarget 
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
